<script>
  import ProviderOverpanelResults from '../ProviderOverpanelResults/ProviderOverpanelResults.svelte';
  import UseClickOff from '../../utils/UseClickOff.svelte';
  import InterceptClick from '../../utils/InterceptClick.svelte';
  import { SPRITESHEET_URL } from '../../utils/constants.ts';
  import { KEYS } from '../../utils/constants';
  import currentLocation from '../../stores/currentLocation';
  import searchValue from '../../stores/search';
  import { page } from '$app/stores';

  let {
    lang,
    providerSearchType,
    inputPlaceHolder,
    searchInstructions,
    buttonText,
    searchingText,
    searchCompleteTemplate,
    errorText,
    emptyErrorText,
    setLocationErrorText,
    providerOverpanelResults,
    centerAligned,
    dataTestId = 'provider-search',
  } = $props();

  const { ARROW_DOWN, ARROW_UP, ESCAPE } = KEYS;
  const langParam = lang === 'es' ? '/es' : '';

  let searchResults = $state([]);
  let numberOfResults = $state(0);
  let searchResultsLabel = $state();
  let container = $state();
  let isError = $state(false);
  let form = $state();
  let input = $state();
  let selected = $state();
  let inputFocused = $state(false);
  let resultsContainer = $state();
  let showInputEmptyMessage = $state(false);
  let resultTiles = [];
  let locationUpdate = $state(false);
  let typeaheadOpen = $derived(inputFocused || selected !== undefined);
  const resultsId = 'provider-search-overpanel-results-list';
  
  searchValue.subscribe((value) => {
    selected = undefined;
    if (!value) searchResults.length = 0;
    if (searchResults.length) inputFocused = true;
  });

  const handleFocus = () => {
    typeaheadOpen = true;
  };

  const handleBlur = () => {
    if (!locationUpdate) {
      isError = false;
      showInputEmptyMessage = false;
      inputFocused = false;
      selected = undefined;
      typeaheadOpen = false;
      numberOfResults = 0;
      input = '';
      $searchValue = '';
    } else {
      if ($currentLocation) {
        isError = false;
        inputFocused = true;
        typeaheadOpen = true;

        performSearch();
      }

      locationUpdate = false;
    }
  };

  const handleKeydown = (e) => {
    if (![ARROW_UP, ARROW_DOWN, ESCAPE].includes(e.key)) return;
    if (e.key === ESCAPE) {
      if (!$searchValue) {
        handleBlur();
        input.blur();
      } else {
        $searchValue = '';
      }

      e.stopPropagation();
      return;
    }

    if (!searchResults.length) return;

    if (selected === undefined) {
      selected = -1;
    }

    selected += e.key === ARROW_DOWN ? 1 : -1;
    if (selected < 0) selected = searchResults.length - 1;
    selected %= searchResults.length;

    e.preventDefault();
  };

  const handleExpandedClick = () => {
    if (input) input.focus();
  };

  let timer, loggedValue;
  const performSearch = async () => {
    showInputEmptyMessage = false;
    isError = false;

    if (!$searchValue) return;

    searchResultsLabel = searchingText;

    searchResults = [];
    numberOfResults = 0;

    let res;
    
    if ($currentLocation) {
      res = await fetch(`/api/postgres/search?lang=${lang}&term=${$searchValue}&type=${providerSearchType}&city=${$currentLocation.city}&zip=${$currentLocation.zip}&state=${$currentLocation.state}&specialty=${$page.data.specialty ? $page.data.specialty : ''}`, {
        method: 'GET',
        headers: { 'content-type': 'application/json' },
      });
      
      searchResults = res.status === 201 ? await res.json() : [];
    } else {
      if (providerSearchType === 'provider' || providerSearchType === 'facility') {
        res = await fetch(`/api/postgres/search?lang=${lang}&term=${$searchValue}&type=${providerSearchType}&specialty=${$page.data.specialty ? $page.data.specialty : ''}`, {
          method: 'GET',
          headers: { 'content-type': 'application/json' },
        });

        searchResults = res.status === 201 ? await res.json() : [];
      }
    }
    
    if (searchResults.length) {
      numberOfResults = searchResults[0].numberOfResults;
      searchResultsLabel = searchCompleteTemplate.replace(/{{NUMBER}}/, numberOfResults);

      if (numberOfResults === 0) {
        searchResultsLabel = errorText;
        isError = true;
      }
    } else {
      searchResultsLabel = errorText;
      isError = true;
    }

    // only log meaningful search terms over 3 characters
    if ($searchValue.length > 3 && loggedValue !== $searchValue) {
      window.umami.track(`${providerSearchType}-search`, { search: $searchValue });
      loggedValue = $searchValue;
    }
  }

  const handleSearch = async () => {
     if (loggedValue !==  $searchValue) {
      window.clearTimeout(timer);
      timer = window.setTimeout(async () => {
        performSearch();
      }, 500);
    }
  };

  const closeCallback = () => {
    locationUpdate = true;
  }
</script>

{#if typeaheadOpen}
  <UseClickOff {container} onClickOffHandler={handleBlur} />
  <InterceptClick parent={resultsContainer} clickHandlerCallback={handleExpandedClick} />
{/if}

{#if inputPlaceHolder && buttonText && providerOverpanelResults}
  <div class="max-w-6xl mx-auto">
    <div bind:this={container} class="relative max-w-152" class:mx-auto={centerAligned}>
      <form
        data-testid={dataTestId}
        class="flex flex-row relative w-full text-gray-gunmetal items-center border-12 rounded-t-lg border-b-0 z-10 pb-1"
        class:border-transparent={!typeaheadOpen}
        class:border-blue-navy={typeaheadOpen}
        class:bg-blue-navy={typeaheadOpen}
        class:mx-auto={centerAligned}
        method="POST"
        bind:this={form}
        action={`${langParam}/doctors-near-me`}
        onsubmit={(e) => e.preventDefault()}
      >
        <div
          class="relative w-full"
          data-testid="{dataTestId}-combobox"
          role="combobox"
          aria-expanded={!!searchResults.length}
          aria-controls={resultsId}
          aria-haspopup="listbox"
        >
          <div aria-live="assertive" aria-label={searchResultsLabel} data-testid="{dataTestId}-combobox-status"></div>
          <input
            class="inline w-full rounded border border-gray-gunmetal bg-white py-2 pl-10 pr-4 leading-loose
            text-base font-normal placeholder-gray-warm focus:relative focus:outline-hidden focus:shadow-focus
            active:shadow-active active:relative"
            class:animate-shake={showInputEmptyMessage}
            data-testid="{dataTestId}-input"
            id="search"
            name="search"
            type="search"
            placeholder={inputPlaceHolder}
            onfocus={handleFocus}
            oninput={handleSearch}
            onkeydown={handleKeydown}
            bind:this={input}
            bind:value={$searchValue}
            autocomplete="off"
            aria-autocomplete="list"
            aria-label={searchInstructions}
            aria-activedescendant={selected !== undefined ? `search-result-${selected}` : ''}
          />
          <svg
            class="absolute top-4 left-4 w-4 h-4 fill-current"
            class:animate-shake={showInputEmptyMessage}
            role="presentation"
          >
            <use xlink:href="{SPRITESHEET_URL}magnifying-glass" />
          </svg>
        </div>
        <button
          type="submit"
          class="flex-1 hidden sm:block ml-3 rounded border-t border-blue-cerulean bg-blue-cerulean py-2 px-4
          text-center text-base leading-loose font-semibold text-white hover-hover:hover:bg-blue-darkish hover-hover:hover:border-blue-darkish
          active:bg-blue-navy active:border-blue-navy whitespace-nowrap focus:relative focus:outline-hidden
          focus:shadow-focus active:shadow-active active:relative"
          onfocus={handleFocus}
        >
          {buttonText}
        </button>
      </form>
      {#if showInputEmptyMessage}
        <div
          class="border-12 border-b-0"
          class:text-blue-navy={!typeaheadOpen}
          class:text-white={typeaheadOpen}
          class:bg-blue-navy={typeaheadOpen}
          class:border-blue-navy={typeaheadOpen}
          class:border-transparent={!typeaheadOpen}
          role="alert"
        >
          {emptyErrorText}
        </div>
      {/if}
      <div
        class="absolute bg-gray-mercury left-0 mt-0 z-40 w-full border-12 border-blue-navy rounded-b-lg"
        class:hidden={!typeaheadOpen}
        class:block={typeaheadOpen}
        bind:this={resultsContainer}
      >
        <ProviderOverpanelResults
          {...providerOverpanelResults}
          id={resultsId}
          {resultTiles}
          {closeCallback}
          currentLocation={$currentLocation}
          {searchResults}
          {numberOfResults}
          {providerSearchType}
          {selected}
          {errorText}
          {setLocationErrorText}
          {isError}
        />
      </div>
    </div>
  </div>
{/if}
